import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useId, useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { AiToolsTabs } from '../ai-tools/AiToolsTabs';
import { AgentTemplatesSection } from './agent-templates';
import { AgentRow } from './AgentRow';
import styles from './AgentsPage.module.css';
import { fetchAgentsPage } from './partner-agents-api';
import type { LaunchDarklyFlags } from '../../types/launch-darkly';

const AGENTS_COPY = {
  currentDescription: "Every tenant's AI agents and the tools they can use.",
  typeColumn: 'Type',
  templateColumn: 'Template',
  description: "Templates decide which tools every tenant's AI agents can use.",
  listHeading: 'Tenant agents',
  listDescription: "Every tenant's agents, the template each one follows, and the tools it can use.",
  emptyNote: 'No agents yet. Agents are created when tenants activate AI features.',
  loadErrorNote: 'Your agents could not be loaded. Refresh the page to try again.',
} as const;

export const AgentsPage = () => {
  const flags = useFlags<LaunchDarklyFlags>();
  const templatesAreEnabled = flags['enable-partner-agent-templates'] === true;
  const description = templatesAreEnabled ? AGENTS_COPY.description : AGENTS_COPY.currentDescription;
  const agentsClassName = templatesAreEnabled ? styles.agents : undefined;
  const typeColumn = templatesAreEnabled ? AGENTS_COPY.templateColumn : AGENTS_COPY.typeColumn;
  const listHeadingId = useId();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['partner-agents', currentPage, pageCursors[currentPage]],
    queryFn: () => fetchAgentsPage(pageCursors[currentPage]),
  });

  // Store the next page's cursor when a page arrives, so Next can request it.
  useEffect(() => {
    if (data?.pagination.hasNextPage && data.pagination.cursor) {
      setPageCursors(prev => ({ ...prev, [currentPage + 1]: data.pagination.cursor ?? undefined }));
    }
  }, [data, currentPage]);

  const agents = data?.agents ?? [];
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = data?.pagination.hasNextPage ?? false;
  const showPager = hasPreviousPage || hasNextPage;

  return (
    <div className={styles.page} data-templates-enabled={templatesAreEnabled}>
      <AiToolsTabs active="agents" />
      <PageHeader title="Agents" description={description} />
      {templatesAreEnabled && <AgentTemplatesSection />}

      <section
        className={agentsClassName}
        aria-labelledby={templatesAreEnabled ? listHeadingId : undefined}>
        {templatesAreEnabled && (
          <>
            <h2 id={listHeadingId}>{AGENTS_COPY.listHeading}</h2>
            <p className={styles.listDescription}>{AGENTS_COPY.listDescription}</p>
          </>
        )}
        {isLoading ? (
          <p className={styles.loading}>Loading…</p>
        ) : isError ? (
          <p className={styles.error}>{AGENTS_COPY.loadErrorNote}</p>
        ) : (
          <>
            {agents.length === 0 ? (
              <p className={styles.empty}>{AGENTS_COPY.emptyNote}</p>
            ) : (
              <Tooltip.Provider delayDuration={100}>
                <div className={styles.agentsTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>External ID</th>
                        <th>{typeColumn}</th>
                        <th>Tools</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map(agent => (
                        <AgentRow key={agent.id} agent={agent} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tooltip.Provider>
            )}
            {showPager && (
              <footer className={styles.pager}>
                <button
                  type="button"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!hasPreviousPage || isFetching}>
                  Previous
                </button>
                <span>Page {currentPage}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNextPage || isFetching}>
                  Next
                </button>
              </footer>
            )}
          </>
        )}
      </section>
    </div>
  );
};
